import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import {
  Search,
  Package,
  ChevronDown,
  ChevronUp,
  Filter,
  Loader,
  RefreshCw,
  Ship,
  List,
  LayoutGrid,
  Eye,
} from 'lucide-react';
import { CourierPackageApi } from '@shared/sdk-v1/apis';
import { CourierPackage } from '@shared/sdk-v1/models';
import { apiConfig } from '../shared/api-config';
import { useAuth } from '../auth/hooks/useAuth';

interface ManifestGroup {
  manifestId: number | null;
  manifestNumber: string;
  packages: CourierPackage[];
  totalWeight: number;
  status: string;
}

const statusBadgeStyle = (status: string): string => {
  switch (status) {
    case 'Ready':
      return 'bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-300';
    case 'In Transit':
      return 'bg-yellow-100 dark:bg-yellow-900 text-yellow-600 dark:text-yellow-300';
    case 'Delivered':
      return 'bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-300';
    case 'At Warehouse':
      return 'bg-purple-100 dark:bg-purple-900 text-purple-600 dark:text-purple-300';
    case 'Pre Alert':
      return 'bg-orange-100 dark:bg-orange-900 text-orange-600 dark:text-orange-300';
    case 'Out for Delivery':
      return 'bg-teal-100 dark:bg-teal-900 text-teal-600 dark:text-teal-300';
    case 'Jamaica Customs':
      return 'bg-indigo-100 dark:bg-indigo-900 text-indigo-600 dark:text-indigo-300';
    case 'Invoice Required':
      return 'bg-red-100 dark:bg-red-900 text-red-600 dark:text-red-300';
    case 'Preparing':
      return 'bg-yellow-100 dark:bg-yellow-900 text-yellow-600 dark:text-yellow-300';
    case 'At Sorting Facility':
      return 'bg-indigo-100 dark:bg-indigo-900 text-indigo-600 dark:text-indigo-300';
    case 'Processing at Customs':
      return 'bg-amber-100 dark:bg-amber-900 text-amber-600 dark:text-amber-300';
    case 'Completed':
      return 'bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-300';
    default:
      return 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300';
  }
};

const invoiceBadgeStyle = (status: string): string => {
  if (status === 'Paid' || status === 'paid') {
    return 'bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-300';
  }
  return 'bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-300';
};

const CourierPackages: React.FC = () => {
  const [searchParams] = useSearchParams();
  const manifestFilter = searchParams.get('manifest');
  const { user, courier } = useAuth();

  const [packages, setPackages] = useState<CourierPackage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [showStatusFilter, setShowStatusFilter] = useState(false);
  const [viewMode, setViewMode] = useState<'manifest' | 'flat'>(() => {
    return (localStorage.getItem('courierPackagesView') as 'manifest' | 'flat') || 'manifest';
  });
  const [expandedManifests, setExpandedManifests] = useState<Set<string>>(new Set());

  const statusOptions = ['All', 'Pre Alert', 'In Transit', 'At Warehouse', 'At Sorting Facility', 'Preparing', 'Ready', 'Out for Delivery', 'Delivered', 'Jamaica Customs', 'Processing at Customs', 'Completed'];

  // Fetch all packages
  const fetchPackages = useCallback(async () => {
    if (!user?.id || !courier?.id) return;

    setIsLoading(true);
    setError(null);

    try {
      const api = new CourierPackageApi(apiConfig());
      const filter: any = {
        where: {
          and: [
            { ownerId: user.id },
            { courierId: courier.id },
          ],
        },
        include: ['invoice'],
        order: ['createdOn DESC'],
        limit: 500,
      };

      const response = await api.courierPackageFind({
        filter: JSON.stringify(filter),
      });

      setPackages(response);
    } catch (err) {
      console.error('Error fetching packages:', err);
      setError('Failed to load packages. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [user?.id, courier?.id]);

  useEffect(() => {
    fetchPackages();
  }, [fetchPackages]);

  // Auto-expand manifest from URL param
  useEffect(() => {
    if (manifestFilter) {
      setExpandedManifests(new Set([manifestFilter]));
      setViewMode('manifest');
    }
  }, [manifestFilter]);

  // Save view mode preference
  useEffect(() => {
    localStorage.setItem('courierPackagesView', viewMode);
  }, [viewMode]);

  // Filter packages by search and status
  const filteredPackages = useMemo(() => {
    let result = packages;

    if (statusFilter !== 'All') {
      result = result.filter((pkg) => pkg.status === statusFilter);
    }

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      result = result.filter((pkg) =>
        (pkg.trackingNo?.toLowerCase() || '').includes(term) ||
        (pkg.description?.toLowerCase() || '').includes(term) ||
        (pkg.merchant?.toLowerCase() || '').includes(term) ||
        (pkg.manifestNumber?.toLowerCase() || '').includes(term)
      );
    }

    return result;
  }, [packages, statusFilter, searchTerm]);

  // Group packages by manifest
  const manifestGroups = useMemo((): ManifestGroup[] => {
    const groupMap = new Map<string, ManifestGroup>();

    filteredPackages.forEach((pkg) => {
      const key = pkg.manifestId ? String(pkg.manifestId) : 'unassigned';
      const existing = groupMap.get(key);

      if (existing) {
        existing.packages.push(pkg);
        existing.totalWeight += pkg.weight || 0;
      } else {
        groupMap.set(key, {
          manifestId: pkg.manifestId || null,
          manifestNumber: pkg.manifestNumber || (pkg.manifestId ? `#${pkg.manifestId}` : 'Unassigned'),
          packages: [pkg],
          totalWeight: pkg.weight || 0,
          status: '', // Will be derived
        });
      }
    });

    // Derive manifest status from packages
    const groups = Array.from(groupMap.values()).map((group) => {
      const statuses = group.packages.map((p) => p.status || '');
      if (statuses.every((s) => s === 'Delivered' || s === 'Completed')) {
        group.status = 'Completed';
      } else if (statuses.some((s) => s === 'In Transit')) {
        group.status = 'In Transit';
      } else if (statuses.some((s) => s === 'At Sorting Facility' || s === 'Preparing')) {
        group.status = 'Preparing';
      } else if (statuses.some((s) => s === 'Ready')) {
        group.status = 'Ready';
      } else {
        group.status = 'Processing';
      }
      return group;
    });

    // Sort: unassigned last, then by most recent package
    groups.sort((a, b) => {
      if (!a.manifestId) return 1;
      if (!b.manifestId) return -1;
      const aDate = a.packages[0]?.createdOn ? new Date(a.packages[0].createdOn).getTime() : 0;
      const bDate = b.packages[0]?.createdOn ? new Date(b.packages[0].createdOn).getTime() : 0;
      return bDate - aDate;
    });

    return groups;
  }, [filteredPackages]);

  const toggleManifest = (key: string) => {
    setExpandedManifests((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const expandAll = () => {
    setExpandedManifests(new Set(manifestGroups.map((g) => String(g.manifestId || 'unassigned'))));
  };

  const collapseAll = () => {
    setExpandedManifests(new Set());
  };

  if (isLoading && packages.length === 0) {
    return (
      <div className="flex flex-col justify-center items-center h-96 space-y-4">
        <Loader className="animate-spin h-8 w-8 text-blue-600" />
        <p className="text-gray-600 dark:text-gray-400">Loading packages...</p>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4 pb-24 md:pb-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Packages</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {filteredPackages.length} package{filteredPackages.length !== 1 ? 's' : ''}
            {viewMode === 'manifest' && ` across ${manifestGroups.length} shipment${manifestGroups.length !== 1 ? 's' : ''}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchPackages}
            className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600
                     text-sm font-medium rounded-md text-gray-700 dark:text-gray-300
                     bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <RefreshCw className="w-4 h-4 mr-1" />
            Refresh
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        {/* Search */}
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search tracking, description, merchant..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                     bg-white dark:bg-gray-800 text-gray-900 dark:text-white
                     focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
          />
        </div>

        {/* Status Filter */}
        <div className="relative">
          <button
            onClick={() => setShowStatusFilter(!showStatusFilter)}
            className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600
                     text-sm font-medium rounded-md text-gray-700 dark:text-gray-300
                     bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 w-full sm:w-auto"
          >
            <Filter className="w-4 h-4 mr-2" />
            {statusFilter}
          </button>

          {showStatusFilter && (
            <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-md shadow-lg
                          border border-gray-300 dark:border-gray-600 z-10 max-h-64 overflow-y-auto">
              <div className="py-1">
                {statusOptions.map((status) => (
                  <button
                    key={status}
                    onClick={() => { setStatusFilter(status); setShowStatusFilter(false); }}
                    className={`block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700
                              ${statusFilter === status ? 'bg-blue-50 dark:bg-blue-900 text-blue-900 dark:text-blue-100' :
                                'text-gray-900 dark:text-white'}`}
                  >
                    {status}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* View Toggle */}
        <div className="flex border border-gray-300 dark:border-gray-600 rounded-md overflow-hidden">
          <button
            onClick={() => setViewMode('manifest')}
            className={`inline-flex items-center px-3 py-2 text-sm font-medium ${
              viewMode === 'manifest'
                ? 'bg-blue-600 text-white'
                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            <LayoutGrid className="w-4 h-4 mr-1" />
            <span className="hidden sm:inline">By Shipment</span>
          </button>
          <button
            onClick={() => setViewMode('flat')}
            className={`inline-flex items-center px-3 py-2 text-sm font-medium ${
              viewMode === 'flat'
                ? 'bg-blue-600 text-white'
                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            <List className="w-4 h-4 mr-1" />
            <span className="hidden sm:inline">All</span>
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4">
          <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
        </div>
      )}

      {/* Empty State */}
      {filteredPackages.length === 0 && !isLoading && (
        <div className="text-center py-12">
          <Package className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No packages found</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {searchTerm ? 'Try adjusting your search.' : 'No packages available.'}
          </p>
        </div>
      )}

      {/* Manifest Grouped View */}
      {viewMode === 'manifest' && manifestGroups.length > 0 && (
        <div className="space-y-3">
          {/* Expand/Collapse controls */}
          <div className="flex justify-end gap-2 text-xs">
            <button onClick={expandAll} className="text-blue-600 dark:text-blue-400 hover:underline">Expand all</button>
            <span className="text-gray-300 dark:text-gray-600">|</span>
            <button onClick={collapseAll} className="text-blue-600 dark:text-blue-400 hover:underline">Collapse all</button>
          </div>

          {manifestGroups.map((group) => {
            const key = String(group.manifestId || 'unassigned');
            const isExpanded = expandedManifests.has(key);

            return (
              <div
                key={key}
                className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 overflow-hidden"
              >
                {/* Manifest Header */}
                <button
                  onClick={() => toggleManifest(key)}
                  className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <Ship className="h-5 w-5 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                    <div className="text-left min-w-0">
                      <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                        {group.manifestNumber}
                      </p>
                    </div>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${
                      statusBadgeStyle(group.status)
                    }`}>
                      {group.status}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 flex-shrink-0 ml-4">
                    <span className="text-xs text-gray-500 dark:text-gray-400 hidden sm:inline">
                      {group.packages.length} pkg{group.packages.length !== 1 ? 's' : ''}
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-400 hidden sm:inline">
                      {group.totalWeight.toFixed(1)} lbs
                    </span>
                    <span className="text-xs font-medium text-gray-700 dark:text-gray-300 sm:hidden">
                      {group.packages.length}
                    </span>
                    {isExpanded ? (
                      <ChevronUp className="h-4 w-4 text-gray-400" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-gray-400" />
                    )}
                  </div>
                </button>

                {/* Packages List */}
                {isExpanded && (
                  <div className="border-t border-gray-200 dark:border-gray-700">
                    {/* Desktop Table */}
                    <div className="hidden md:block overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-900">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Tracking</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Description</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Weight</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Invoice</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Created</th>
                            <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">View</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                          {group.packages.map((pkg) => (
                            <tr key={pkg.id} className="hover:bg-gray-50 dark:hover:bg-gray-750">
                              <td className="px-4 py-3">
                                <p className="text-sm font-mono font-medium text-gray-900 dark:text-white truncate max-w-[200px]">
                                  {pkg.trackingNo}
                                </p>
                              </td>
                              <td className="px-4 py-3">
                                <p className="text-sm text-gray-700 dark:text-gray-300 truncate max-w-[180px]">
                                  {pkg.description || '—'}
                                </p>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">
                                {pkg.weight ? `${pkg.weight} lbs` : '—'}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusBadgeStyle(pkg.status || 'Unknown')}`}>
                                  {pkg.status || 'Unknown'}
                                </span>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                {(pkg as any).invoice ? (
                                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${invoiceBadgeStyle((pkg as any).invoice?.status || '')}`}>
                                    {(pkg as any).invoice?.status === 'paid' ? 'Paid' : 'Open'}
                                  </span>
                                ) : (
                                  <span className="text-xs text-gray-400">—</span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
                                {pkg.createdOn
                                  ? new Date(pkg.createdOn).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
                                  : '—'}
                              </td>
                              <td className="px-4 py-3 text-right">
                                <Link
                                  to={`/packages/${pkg.id}`}
                                  className="text-blue-600 dark:text-blue-400 hover:underline"
                                >
                                  <Eye className="w-4 h-4" />
                                </Link>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Mobile Cards */}
                    <div className="md:hidden divide-y divide-gray-100 dark:divide-gray-700">
                      {group.packages.map((pkg) => (
                        <Link
                          key={pkg.id}
                          to={`/packages/${pkg.id}`}
                          className="block px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-750"
                        >
                          <div className="flex items-center justify-between mb-1">
                            <p className="text-sm font-mono font-medium text-gray-900 dark:text-white truncate max-w-[200px]">
                              {pkg.trackingNo}
                            </p>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusBadgeStyle(pkg.status || 'Unknown')}`}>
                              {pkg.status || 'Unknown'}
                            </span>
                          </div>
                          <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
                            {pkg.description && <span className="truncate max-w-[120px]">{pkg.description}</span>}
                            {pkg.weight && <span>{pkg.weight} lbs</span>}
                            {pkg.createdOn && (
                              <span>{new Date(pkg.createdOn).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</span>
                            )}
                            {(pkg as any).invoice && (
                              <span className={`px-1.5 py-0.5 rounded-full text-xs font-medium ${invoiceBadgeStyle((pkg as any).invoice?.status || '')}`}>
                                {(pkg as any).invoice?.status === 'paid' ? 'Paid' : 'Open'}
                              </span>
                            )}
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Flat List View */}
      {viewMode === 'flat' && filteredPackages.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 overflow-hidden">
          {/* Desktop Table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Tracking</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Description</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Shipment</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Weight</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Invoice</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Created</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">View</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {filteredPackages.map((pkg) => (
                  <tr key={pkg.id} className="hover:bg-gray-50 dark:hover:bg-gray-750">
                    <td className="px-4 py-3">
                      <p className="text-sm font-mono font-medium text-gray-900 dark:text-white truncate max-w-[200px]">
                        {pkg.trackingNo}
                      </p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-gray-700 dark:text-gray-300 truncate max-w-[150px]">
                        {pkg.description || '—'}
                      </p>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">
                      {pkg.manifestNumber || (pkg.manifestId ? `#${pkg.manifestId}` : '—')}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">
                      {pkg.weight ? `${pkg.weight} lbs` : '—'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusBadgeStyle(pkg.status || 'Unknown')}`}>
                        {pkg.status || 'Unknown'}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {(pkg as any).invoice ? (
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${invoiceBadgeStyle((pkg as any).invoice?.status || '')}`}>
                          {(pkg as any).invoice?.status === 'paid' ? 'Paid' : 'Open'}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
                      {pkg.createdOn
                        ? new Date(pkg.createdOn).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
                        : '—'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Link
                        to={`/packages/${pkg.id}`}
                        className="text-blue-600 dark:text-blue-400 hover:underline"
                      >
                        <Eye className="w-4 h-4" />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden divide-y divide-gray-100 dark:divide-gray-700">
            {filteredPackages.map((pkg) => (
              <Link
                key={pkg.id}
                to={`/packages/${pkg.id}`}
                className="block px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-750"
              >
                <div className="flex items-center justify-between mb-1">
                  <p className="text-sm font-mono font-medium text-gray-900 dark:text-white truncate max-w-[200px]">
                    {pkg.trackingNo}
                  </p>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusBadgeStyle(pkg.status || 'Unknown')}`}>
                    {pkg.status || 'Unknown'}
                  </span>
                </div>
                <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
                  {pkg.description && <span className="truncate max-w-[120px]">{pkg.description}</span>}
                  {pkg.weight && <span>{pkg.weight} lbs</span>}
                  {pkg.manifestNumber && <span>{pkg.manifestNumber}</span>}
                  {pkg.createdOn && (
                    <span>{new Date(pkg.createdOn).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</span>
                  )}
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CourierPackages;
